import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, GridLegacy as Grid, Link, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPagination } from '@mui/x-data-grid';

import { getRegionNameByCode } from '@luxuryescapes/lib-regions';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import Spinner from '~/components/Common/Spinner';

import { useCreateRegistrationToken } from '~/hooks/apis/agent/useCreateRegistrationToken';
import { useDeleteEoi } from '~/hooks/apis/agent/useDeleteEoi';
import { useListRegistrationTokens } from '~/hooks/apis/agent/useListRegistrationTokens';
import { useListUserEmailLogs } from '~/hooks/apis/agent/useListUserEmailLogs';
import { useResendActivationEmail } from '~/hooks/apis/agent/useResendActivationEmail';
import { useUpdateEoiStatus } from '~/hooks/apis/agent/useUpdateEoiStatus';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { addHours, formatDateDateTimeWidget, formatDateShortDD } from '~/services/TimeService';

import { Eoi, EoiStatus } from '~/types/services/agentHub';

import { titleCase } from '~/utils/stringUtils';
import { mountTenantOnboardLink } from '~/utils/tenantUtils';

const columns: Array<GridColDef> = [
  { field: 'status', headerName: 'Status', sortable: false, display: 'flex' },
  { field: 'sentAt', headerName: 'Sent At', flex: 1, sortable: false, display: 'flex' },
  { field: 'type', headerName: 'Type', flex: 1, sortable: false, display: 'flex' },
  { field: 'failureMessage', headerName: 'Error Message', flex: 1, sortable: false, display: 'flex' },
];

const SIZE_PER_PAGE = 4;

export interface AgencySubmissionDetailsProps {
  eoiDetails: Eoi;
  setEditing: (value: boolean) => void;
  onClose: () => void;
}

export default function AgencySubmissionDetails({ eoiDetails, setEditing, onClose }: AgencySubmissionDetailsProps) {
  const { enqueueSnackbar } = useSnackbar();

  const { tenant: currentTenant } = useCurrentTenant();

  const [emailLogsPage, setEmailLogsPage] = useState(0);
  const [registrationTokensPage, setRegistrationTokensPage] = useState(0);
  const [expiresAt, setExpiresAt] = useState<string>(formatDateDateTimeWidget(addHours(48, new Date())));

  const { mutate: updateEoiStatus, isPending: isUpdatingEoiStatus } = useUpdateEoiStatus();
  const deleteEoiMutation = useDeleteEoi();
  const resendActivationEmailMutation = useResendActivationEmail();
  const createRegistrationTokenMutation = useCreateRegistrationToken();

  const { data: emailLogsData, isLoading: isLoadingEmailLogs } = useListUserEmailLogs({
    agentId: eoiDetails.agentId,
    page: emailLogsPage,
    pageSize: SIZE_PER_PAGE,
  });

  const { data: registrationTokensData, isLoading: isLoadingRegistrationTokens } = useListRegistrationTokens({
    agentId: eoiDetails.agentId,
    status: eoiDetails.status,
    page: registrationTokensPage,
    pageSize: SIZE_PER_PAGE,
  });

  const emailLogs = emailLogsData?.rows ?? [];
  const totalEmailLogs = emailLogsData?.total ?? 0;
  const registrationTokens = registrationTokensData?.rows ?? [];
  const totalRegistrationTokens = registrationTokensData?.total ?? 0;

  const isLoading = isLoadingEmailLogs || isLoadingRegistrationTokens;

  const copyOnboardLink = useCallback(
    (token: string, region: string) => {
      navigator.clipboard.writeText(mountTenantOnboardLink(currentTenant, token, region));
      enqueueSnackbar(`Copied to clipboard`, { variant: 'success' });
    },
    [enqueueSnackbar, currentTenant],
  );

  const registrationTokenColumns: Array<GridColDef> = useMemo(() => {
    return [
      { field: 'id', headerName: 'ID', flex: 1, sortable: false, display: 'flex' },
      { field: 'createdAt', headerName: 'Created At', flex: 1, sortable: false, display: 'flex' },
      {
        field: 'expiresAt',
        headerName: 'Expires At',
        flex: 1,
        sortable: false,
        display: 'flex',
        renderCell: (params) => {
          if (params.row.expired) {
            return 'Expired';
          }
          return params.row.expiresAt;
        },
      },
      { field: 'isUsed', headerName: 'Used', flex: 1, sortable: false, display: 'flex' },
      {
        field: 'action',
        headerName: '',
        flex: 1,
        renderCell: (params) => {
          if (params.row.expired || params.row.isUsed) {
            return null;
          }
          return (
            <Button variant="text" onClick={() => copyOnboardLink(params.row.id, eoiDetails.regionCode.toLowerCase())}>
              Copy Onboard Link
            </Button>
          );
        },
        display: 'flex',
      },
    ];
  }, [copyOnboardLink, eoiDetails.regionCode]);

  function updateSubmission(status: 'approved' | 'rejected') {
    updateEoiStatus(
      { id: eoiDetails.id, status },
      {
        onSuccess: () => {
          enqueueSnackbar(`Submission ${status === 'approved' ? 'approved' : 'rejected'}`, { variant: 'success' });
        },
        onError: (error) => {
          enqueueSnackbar(`Failed to update submission: ${error.message}`, { variant: 'error' });
        },
      },
    );
  }

  function handleResendActivationEmail() {
    resendActivationEmailMutation.mutate(eoiDetails.id, {
      onSuccess: () => {
        enqueueSnackbar('Activation email sent successfully', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to resend activation email: ${error.message}`, { variant: 'error' });
      },
    });
  }

  function handleDeleteSubmission() {
    deleteEoiMutation.mutate(eoiDetails.id, {
      onSuccess: () => {
        enqueueSnackbar('Submission deleted', { variant: 'success' });
        onClose();
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to delete submission: ${error.message}`, { variant: 'error' });
      },
    });
  }

  function handleCreateRegistrationToken() {
    createRegistrationTokenMutation.mutate(
      { agentId: eoiDetails.agentId, expiresAt },
      {
        onSuccess: () => {
          enqueueSnackbar('Registration token created successfully', { variant: 'success' });
        },
        onError: (error) => {
          enqueueSnackbar(`Failed to create registration token: ${error.message}`, { variant: 'error' });
        },
      },
    );
  }

  return (
    <Grid container direction="column" wrap="nowrap" columns={12}>
      {isLoading && <Spinner />}
      {!isLoading && (
        <>
          <Typography variant="h5">Agency</Typography>
          <Grid item mt={1}>
            <Typography>Agency Name:</Typography>
            {eoiDetails.agencyId && (
              <Link
                href={`/users-list/agencies?id=${eoiDetails.agencyId}`}
                target="_blank"
                rel="noreferrer"
                fontWeight="bold"
              >
                {eoiDetails.agencyName}
              </Link>
            )}
            {!eoiDetails.agencyId && <Typography fontWeight="bold">{eoiDetails.agencyName}</Typography>}
          </Grid>

          <Grid item mt={1}>
            <Typography>Affiliation:</Typography>
            <Typography fontWeight="bold">{eoiDetails.affiliation}</Typography>
          </Grid>

          <Grid item mt={1}>
            <Typography>Region:</Typography>
            <Typography fontWeight="bold">
              {eoiDetails.regionCode} - {getRegionNameByCode(eoiDetails.regionCode)}
            </Typography>
          </Grid>

          <Typography mt={2} variant="h5">
            User
          </Typography>

          <Grid item mt={1}>
            <Typography>Name:</Typography>
            <Typography fontWeight="bold">
              {eoiDetails.givenName} {eoiDetails.surname}
            </Typography>
          </Grid>

          <Grid item mt={1}>
            <Typography>Email:</Typography>
            {eoiDetails.customerId && (
              <Link
                sx={{ wordBreak: 'break-all' }}
                href={`/users/${eoiDetails.customerId}`}
                target="_blank"
                rel="noreferrer"
                fontWeight="bold"
              >
                {eoiDetails.email}
              </Link>
            )}
            {!eoiDetails.customerId && (
              <Typography sx={{ wordBreak: 'break-all' }} textOverflow="clip" fontWeight="bold">
                {eoiDetails.email}
              </Typography>
            )}
          </Grid>
          <Grid item mt={1}>
            <Typography>Phone:</Typography>
            <Typography fontWeight="bold">{eoiDetails.phone}</Typography>
          </Grid>
          <Grid item mt={1}>
            <Typography>Submitted At:</Typography>
            <Typography fontWeight="bold">{formatDateShortDD(eoiDetails.submittedAt)}</Typography>
          </Grid>
          <Grid item mt={1} display="flex" direction="row">
            <Stack>
              <Typography>Status:</Typography>
              <Typography fontWeight="bold">{titleCase(eoiDetails.status)}</Typography>
            </Stack>
            {eoiDetails.onboardedAt && (
              <Stack ml={2}>
                <Typography>Onboarded At:</Typography>
                <Typography fontWeight="bold">{formatDateShortDD(eoiDetails.onboardedAt)}</Typography>
              </Stack>
            )}
            {(eoiDetails.status === EoiStatus.Approved || eoiDetails.status === EoiStatus.Onboarded) && (
              <Stack ml={2}>
                <Typography>Approved At: </Typography>
                <Typography fontWeight="bold">
                  {eoiDetails.approvedAt && formatDateShortDD(eoiDetails.approvedAt)}
                </Typography>
              </Stack>
            )}
            {eoiDetails.status === EoiStatus.Rejected && (
              <Stack ml={2}>
                <Typography>Rejected At: </Typography>
                <Typography fontWeight="bold">
                  {eoiDetails.rejectedAt && formatDateShortDD(eoiDetails.rejectedAt)}
                </Typography>
              </Stack>
            )}
          </Grid>
          <Grid item mt={1}>
            <Typography>Referrer’s email address:</Typography>
            <Typography sx={{ wordBreak: 'break-all' }} fontWeight="bold">
              {eoiDetails.referrerEmail || 'Null'}
            </Typography>
          </Grid>
          {[EoiStatus.Pending, EoiStatus.Rejected].includes(eoiDetails.status) && (
            <Grid item mt={2} display="flex" justifyContent="space-around" direction="row">
              <Button
                disabled={isUpdatingEoiStatus}
                variant="contained"
                color="secondary"
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
              <Button disabled={isUpdatingEoiStatus} variant="contained" onClick={() => updateSubmission('approved')}>
                Approve
              </Button>

              {eoiDetails.status !== EoiStatus.Rejected && (
                <Button
                  disabled={isUpdatingEoiStatus}
                  variant="contained"
                  color="error"
                  onClick={() => updateSubmission('rejected')}
                >
                  Reject
                </Button>
              )}
              {EoiStatus.Pending === eoiDetails.status && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteSubmission}
                  disabled={deleteEoiMutation.isPending || isUpdatingEoiStatus}
                >
                  {deleteEoiMutation.isPending ? 'Deleting...' : 'Delete Submission'}
                </Button>
              )}
            </Grid>
          )}

          {[EoiStatus.Approved, EoiStatus.Onboarded].includes(eoiDetails.status) && (
            <>
              {EoiStatus.Approved === eoiDetails.status && (
                <Grid item mt={2} display="flex" justifyContent="space-around" direction="row">
                  <Button variant="contained" color="secondary" onClick={() => setEditing(true)}>
                    Edit Details
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteSubmission}
                    disabled={deleteEoiMutation.isPending}
                  >
                    {deleteEoiMutation.isPending ? 'Deleting...' : 'Delete Submission'}
                  </Button>
                </Grid>
              )}
              <Typography mt={2} variant="h5">
                Email Logs
              </Typography>
              <Grid item mt={2}>
                <DataGrid
                  rows={emailLogs}
                  rowCount={totalEmailLogs}
                  columns={columns}
                  loading={isLoadingEmailLogs}
                  pagination
                  paginationMode="server"
                  paginationModel={{ page: emailLogsPage, pageSize: SIZE_PER_PAGE }}
                  pageSizeOptions={[SIZE_PER_PAGE]}
                  onPaginationModelChange={({ page: newPage }) => setEmailLogsPage(newPage)}
                  slots={{ pagination: GridPagination }}
                  getRowId={(row) => row.sentAt}
                  disableColumnFilter
                  disableColumnMenu
                  disableRowSelectionOnClick
                  autoHeight
                />
                <Grid item mt={2} display="flex" justifyContent="space-between" direction="row-reverse">
                  <Button
                    title="This will use the last valid token or create a new one if needed"
                    variant="contained"
                    onClick={handleResendActivationEmail}
                    disabled={resendActivationEmailMutation.isPending}
                  >
                    {resendActivationEmailMutation.isPending ? 'Sending...' : 'Re-send Activation Email'}
                  </Button>
                </Grid>
              </Grid>
              <Typography mt={2} variant="h5">
                Registration Tokens
              </Typography>
              <Grid item mt={2}>
                <DataGrid
                  rows={registrationTokens}
                  rowCount={totalRegistrationTokens}
                  columns={registrationTokenColumns}
                  loading={isLoadingRegistrationTokens}
                  pagination
                  paginationMode="server"
                  paginationModel={{ page: registrationTokensPage, pageSize: SIZE_PER_PAGE }}
                  pageSizeOptions={[SIZE_PER_PAGE]}
                  onPaginationModelChange={({ page: newPage }) => setRegistrationTokensPage(newPage)}
                  slots={{ pagination: GridPagination }}
                  getRowId={(row) => row.createdAt}
                  disableColumnFilter
                  disableColumnMenu
                  disableRowSelectionOnClick
                  autoHeight
                />
                <Grid item mt={2} display="flex" justifyContent="flex-end" direction="row">
                  <DateTimeWidget
                    disablePast
                    format="MM/DD/YYYY HH:mm:ss"
                    label="Expires At"
                    value={expiresAt}
                    onChange={(value) => setExpiresAt(value)}
                  />
                  <Button
                    sx={{ ml: 2 }}
                    variant="contained"
                    onClick={handleCreateRegistrationToken}
                    disabled={createRegistrationTokenMutation.isPending}
                  >
                    {createRegistrationTokenMutation.isPending ? 'Creating...' : 'Create Registration Token'}
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  );
}
